// Menurunkan seluruh palet aksen (shade/tint/varian aktif & hover) dari satu warna dasar
// (settings.app_color). Dipakai supaya SEMUA warna aksen di web (bukan cuma tombol utama)
// ikut berubah otomatis kalau admin ganti "Warna Utama" di Pengaturan Situs.

function hexToRgb(hex) {
  const m = hex.replace('#', '').match(/.{1,2}/g);
  return { r: parseInt(m[0], 16), g: parseInt(m[1], 16), b: parseInt(m[2], 16) };
}

function rgbToHex(r, g, b) {
  const c = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb(h, s, l) {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) { const v = l * 255; return { r: v, g: v, b: v }; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hue2rgb(p, q, h + 1 / 3) * 255,
    g: hue2rgb(p, q, h) * 255,
    b: hue2rgb(p, q, h - 1 / 3) * 255,
  };
}

// Geser lightness (persen, -100..100) dari sebuah hex, tetap pakai hue & saturation asli.
function shade(hex, deltaL) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  const nl = Math.max(4, Math.min(96, l + deltaL));
  const { r: nr, g: ng, b: nb } = hslToRgb(h, s, nl);
  return rgbToHex(nr, ng, nb);
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;
}

export function deriveAccentPalette(baseHex) {
  const base = /^#[0-9a-fA-F]{6}$/.test(baseHex) ? baseHex : '#4A7A3A';
  const { l } = rgbToHsl(...Object.values(hexToRgb(base)));

  // Delta lightness (persen) dikalibrasi dari palet manual asli (#4A7A3A dkk) supaya warna
  // default menghasilkan hasil yang (hampir) identik, lalu tetap masuk akal untuk warna lain.
  const base400 = shade(base, 8);
  const base600 = shade(base, -8);
  const base700 = shade(base, -13);

  // Teks/ikon "aktif": gelap di atas bg terang, terang di atas bg gelap — jaga jarak minimum
  // dari base supaya tetap kebaca di kedua mode meski base-nya sendiri sudah gelap/terang.
  const activeTextLight = shade(base, Math.min(-11, 22 - l));
  const activeTextDark = shade(base, Math.max(22, 60 - l));

  // Pill background state aktif — tint sangat muda (mode terang) / shade sangat gelap (mode gelap).
  const pillBgLight = shade(base, Math.max(48, 90 - l));
  const pillBgDark = shade(base, Math.min(-16, 8 - l));

  return {
    '--color-forest-400': base400,
    '--color-forest-500': base,
    '--color-forest-600': base600,
    '--color-forest-700': base700,
    '--accent': base,
    '--accent-hover': base600,
    '--accent-active-text': activeTextLight,
    '--accent-active-text-dark': activeTextDark,
    '--accent-pill-bg': pillBgLight,
    '--accent-pill-bg-dark': pillBgDark,
    '--accent-hover-bg': rgba(base, 0.08),
    '--accent-hover-bg-dark': rgba(activeTextDark, 0.12),
    '--accent-soft': rgba(base, 0.1),
    '--accent-soft-border': rgba(base, 0.3),
    '--accent-gradient-to': base600,
    '--accent-icon-gradient-to': base400,
  };
}

// Terapkan palet ke CSS custom properties di <html>, supaya semua utility Tailwind
// (text-forest-500, bg-forest-600, dll — lihat @theme di index.css) dan semua
// `var(--accent-...)` di komponen ikut berubah otomatis.
export function applyAccentPalette(baseHex) {
  const palette = deriveAccentPalette(baseHex);
  const root = document.documentElement.style;
  Object.entries(palette).forEach(([key, value]) => {
    if (key.startsWith('--')) root.setProperty(key, value);
  });
}
